import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { supabase } from '../supabaseClient';
import UnlockPaymentModal from '../components/UnlockPaymentModal';

interface House {
  id: string;
  title: string;
  building_name: string;
  location: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  type: string;
  description: string;
  whatsapp?: string;
  google_maps_link?: string;
  unlock_fee: number;
  images: string[];
  is_available: boolean;
  video_url?: string;
  lister_id?: string;
}

const HousesForSalePage = () => {
  const [listings, setListings] = useState<House[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<House | null>(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [galleryImageLoading, setGalleryImageLoading] = useState(true);
  const [unlockingItem, setUnlockingItem] = useState<House | null>(null);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('listing');
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const fetchListings = async () => {
      const { data, error } = await supabase
        .from('houses_for_sale')
        .select('*')
        .eq('is_available', true)
        .neq('status', 'banned')
        .order('created_at', { ascending: false });
      if (!error && data) setListings(data);
      setLoading(false);
    };
    fetchListings();
  }, []);

  useEffect(() => {
    if (highlightId && cardRefs.current[highlightId]) {
      cardRefs.current[highlightId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const alreadyLogged = sessionStorage.getItem(`logged-${highlightId}`);
      if (!alreadyLogged) {
        const item = listings.find(l => l.id === highlightId);
        if (item) {
          supabase.from('link_clicks').insert({
            listing_id: item.id,
            listing_table: 'houses_for_sale',
            lister_id: (item as any).lister_id || null,
          }).then(() => {
            sessionStorage.setItem(`logged-${highlightId}`, 'true');
          });
        }
      }
    }
  }, [highlightId, listings]);

  useEffect(() => {
    if (!selected) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setPhotoIndex(i => (i + 1) % selected.images.length);
      if (e.key === 'ArrowLeft') setPhotoIndex(i => (i - 1 + selected.images.length) % selected.images.length);
      if (e.key === 'Escape') setSelected(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selected]);

  useEffect(() => {
    setGalleryImageLoading(true);
  }, [photoIndex]);

  const openGallery = (item: House) => {
    setSelected(item);
    setPhotoIndex(0);
    setGalleryImageLoading(true);
  };

  const handleUnlock = (item: House) => {
    setUnlockingItem(item);
  };

  const handleShare = (item: House) => {
    const url = `${window.location.origin}/houses-for-sale?listing=${item.id}`;
    const message = `Check out this listing on One Stop Shop: ${item.title}\n${url}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--dark)', paddingTop: '100px', paddingBottom: '80px' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
        <BackButton />
        <p style={{ color: 'var(--gold)', fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '12px' }}>Browse Listings</p>
        <h1 className="serif" style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 300, color: 'var(--text)', marginBottom: '8px' }}>
          Houses for <span style={{ color: 'var(--gold)' }}>Sale</span>
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px' }}>Find your dream home across Kenya's finest neighborhoods.</p>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-muted)' }}>
            <p>Loading listings...</p>
          </div>
        ) : listings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-muted)' }}>
            <p style={{ fontSize: '48px', marginBottom: '16px' }}>🏠</p>
            <p style={{ fontSize: '18px', marginBottom: '8px', color: 'var(--text)' }}>No listings yet</p>
            <p style={{ fontSize: '14px' }}>Check back soon — new properties are added regularly.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {listings.map((item) => (
              <div key={item.id}
                ref={el => { cardRefs.current[item.id] = el; }}
                className="card"
                style={{
                  boxShadow: highlightId === item.id ? '0 0 0 2px var(--gold)' : 'none',
                  transition: 'box-shadow 0.3s ease',
                }}>
                <div
                  onClick={() => item.images && item.images.length > 1 && openGallery(item)}
                  style={{ height: '200px', background: 'var(--dark-4)', position: 'relative', overflow: 'hidden', cursor: item.images && item.images.length > 1 ? 'pointer' : 'default' }}>
                  {item.images && item.images.length > 0 ? (
                    <img src={item.images[0]} alt={item.title} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px' }}>🏠</div>
                  )}
                  {item.images && item.images.length > 1 && (
                    <div style={{ position: 'absolute', top: '12px', right: '12px', background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '11px', padding: '4px 8px', borderRadius: '4px' }}>
                      📷 {item.images.length} — tap to view all
                    </div>
                  )}
                </div>
                <div style={{ padding: '24px' }}>
                  <span className="tag">{item.type}</span>
                  <h3 style={{ color: 'var(--text)', fontSize: '17px', fontWeight: 500, margin: '12px 0 4px' }}>{item.title}</h3>
                  {item.building_name && <p style={{ color: 'var(--gold)', fontSize: '12px', marginBottom: '6px' }}>🏢 {item.building_name}</p>}
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '4px' }}>📍 {item.location}</p>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>🛏️ {item.bedrooms} Beds</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>🚿 {item.bathrooms} Baths</span>
                  </div>
                  {item.description && <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6, marginBottom: '16px' }}>{item.description}</p>}
                  {item.video_url && (
                    <a href={item.video_url} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--gold)', fontSize: '13px', textDecoration: 'none', marginBottom: '16px' }}>
                      ▶ Watch Video Tour
                    </a>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <span style={{ color: 'var(--gold)', fontWeight: 600, fontSize: '20px' }}>KSh {item.price.toLocaleString()}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                      Unlock fee: KSh {item.unlock_fee}
                    </span>
                  </div>
                  <button
                    onClick={() => handleUnlock(item)}
                    className="btn-primary"
                    style={{ width: '100%', padding: '12px', fontSize: '13px', marginBottom: '8px' }}
                  >
                    🔒 Unlock Location & Contact
                  </button>
                  <button onClick={() => handleShare(item)}
                    style={{ width: '100%', marginTop: '8px', background: 'transparent', border: '1px solid rgba(201,168,76,0.2)', color: copiedId === item.id ? '#4ade80' : 'var(--text-muted)', padding: '10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                    {copiedId === item.id ? '✅ Link copied!' : '🔗 Share this listing'}
                  </button>
                  {item.images && item.images.length > 1 && (
                    <button onClick={() => openGallery(item)}
                      style={{ width: '100%', marginTop: '8px', background: 'transparent', border: '1px solid rgba(201,168,76,0.2)', color: 'var(--text-muted)', padding: '10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                      View all {item.images.length} photos
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div onClick={() => setSelected(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '1100px', width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ color: 'var(--text)', fontSize: '18px' }}>{selected.title}</h3>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '28px', cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
              <button
                onClick={() => setPhotoIndex(i => (i - 1 + selected.images.length) % selected.images.length)}
                style={{ position: 'absolute', left: '0', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(201,168,76,0.3)', color: 'var(--gold)', width: '44px', height: '44px', borderRadius: '50%', fontSize: '20px', cursor: 'pointer', zIndex: 2 }}>
                ‹
              </button>
              {galleryImageLoading && (
                <div style={{ position: 'absolute', color: 'var(--text-muted)', fontSize: '13px' }}>Loading photo...</div>
              )}
              <img src={selected.images[photoIndex]} alt={`photo-${photoIndex}`}
                decoding="async"
                onLoad={() => setGalleryImageLoading(false)}
                onError={() => setGalleryImageLoading(false)}
                style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', borderRadius: '8px', opacity: galleryImageLoading ? 0 : 1, transition: 'opacity 0.2s' }} />
              <button
                onClick={() => setPhotoIndex(i => (i + 1) % selected.images.length)}
                style={{ position: 'absolute', right: '0', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(201,168,76,0.3)', color: 'var(--gold)', width: '44px', height: '44px', borderRadius: '50%', fontSize: '20px', cursor: 'pointer', zIndex: 2 }}>
                ›
              </button>
            </div>

            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
              {photoIndex + 1} / {selected.images.length}
            </p>

            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
              {selected.images.map((url, i) => (
                <img key={i} src={url} alt={`thumb-${i}`}
                  loading="lazy"
                  decoding="async"
                  onClick={() => setPhotoIndex(i)}
                  style={{
                    width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', flexShrink: 0,
                    border: i === photoIndex ? '2px solid var(--gold)' : '2px solid transparent',
                    opacity: i === photoIndex ? 1 : 0.6,
                  }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {unlockingItem && (
        <UnlockPaymentModal
          listingId={unlockingItem.id}
          listingTable="houses_for_sale"
          listingTitle={unlockingItem.title}
          unlockFee={unlockingItem.unlock_fee}
          onClose={() => setUnlockingItem(null)}
        />
      )}
    </div>
  );
};

export default HousesForSalePage;